package com.dynamicruntime.common.user

// Admin (user-management) constants shared with the *frontend*, alongside the auth constants in
// AuthConstants.kt and for the same reason: an admin console is a Kotlin/JS widget-group waiting to be written,
// and it should build its calls from the same strings the backend serves them under. Per the code guide, these
// are lowerCamelCase `const val`s in short upper-case acronym objects, always referenced qualified.

import com.dynamicruntime.common.endpoint.clientPath
import com.dynamicruntime.common.http.request.SECT

/**
 * **Full-scope** admin endpoint paths (before the API context root is prepended). Every one sits under the
 * `admin` *section*, which `RequestService.adminSections` gates on
 * [com.dynamicruntime.common.http.request.ROLE.allClients] -- the path prefix is the access control, so an
 * endpoint added here reaches every client by construction (issue #225).
 *
 * The same endpoints exist under [UADEP] for an administrator confined to their own client. A caller who holds
 * the capability satisfies both, and gets identical answers either way, since their scope is unrestricted.
 */
@Suppress("ConstPropertyName")
object ADEP {
    const val users = "/${SECT.admin}/users"

    /**
     * The brute-force search over the user cache (issue #411): richer than [users], which is a
     * newest-first SQL listing. This one searches and sorts *active* users in memory (email/name substring,
     * client, update-time range) and is the surface the console's search should call.
     */
    const val userSearch = "/${SECT.admin}/userSearch"
    const val userCreate = "/${SECT.admin}/user/create"
    const val userSetRoles = "/${SECT.admin}/user/setRoles"
    const val userSetEnabled = "/${SECT.admin}/user/setEnabled"
    const val userSetOrg = "/${SECT.admin}/user/setOrg"
    const val userSetName = "/${SECT.admin}/user/setName"
    /** Replaces a user's free-form labels (issue #786). */
    const val userSetLabels = "/${SECT.admin}/user/setLabels"
    /** A client's suggested user labels (issue #786) -- what a label editor offers, without binding to it. */
    const val userLabelSuggestions = "/${SECT.admin}/user/labelSuggestions"
    /** (Re)sends the invitation for a user nobody has claimed yet (issue #751). */
    const val userInvite = "/${SECT.admin}/user/invite"

    /** Mails a claimed user a reminder of where and how to sign in (issue #1504): the ordinary sign-in page, no link that signs in. */
    const val userRemind = "/${SECT.admin}/user/remind"
    /**
     * The person behind a user (issue #770): their identity's facts and the other users of that identity the
     * caller may administer -- what the Users page's editor shows above the editable data.
     */
    const val userIdentity = "/${SECT.admin}/user/identity"
    /**
     * The verb says what happens; the path names the resource (issue #335). Its input rides in the query
     * string, since a DELETE carries no body -- see [com.dynamicruntime.common.endpoint.HttpMethod.DELETE].
     */
    const val userDelete = "/${SECT.admin}/user"

    /**
     * The clients this deployment carries (issue #343).
     *
     * Full-scope only, and it belongs here rather than under [UADEP] for a reason that is not merely tidiness:
     * a cross-client view is not a client-scoped administrator's business. Somebody confined to one client has
     * no question this listing answers that their own client's definition does not.
     */
    const val clients = "/${SECT.admin}/clients"

    /**
     * Creates a client from data (issue #1054): its definition written as its first stored configuration, published,
     * and loaded. Full-scope, as every client's creation is the platform operator's: there is no client to be the
     * administrator of until it exists.
     */
    const val clientCreate = "/${SECT.admin}/client/create"

    /**
     * Retires a client, or brings a retired one back (issue #1111): its data kept, and the client absent everywhere
     * while retired. Full-scope, as only the platform decides whether a client is there at all.
     */
    const val clientRetirementSet = "/${SECT.admin}/client/retirement/set"

    /**
     * One client's full definition — its attributes, its traits (with their data schema), its trait-usage rules,
     * and its workflow ids — for an `allClients` admin who needs to see or act in another client's rules (issue
     * #672). Full-scope only, the same reason [clients] is: it names a client the caller need not belong to.
     */
    const val clientDefinition = "/${SECT.admin}/client/definition"

    /**
     * A cross-client overview: every present client with a little summary (its workflow ids, trait ids, and
     * usage-column labels), the companion listing to [clientDefinition] (issue #672). Distinct from [clients],
     * which is the lightweight `ClientInfo` listing the user-admin client picker reads.
     */
    const val clientSummaries = "/${SECT.admin}/clients/summary"
}

/**
 * **Scoped** user-administration paths: the same operations as [ADEP], reachable by any
 * [com.dynamicruntime.common.http.request.ROLE.admin] and confined to what their `ReadScope` allows (issue
 * #225). Its `clientAdmin` section is what a client-scoped administrator has instead of a narrowed view of the
 * full-scope surface.
 *
 * The section is named for **authority**, not topic (issue #466): `clientAdmin` says "an administrator confined
 * to one client's scope", which is the statement a section makes. It sits opposite `admin` (the deployment-wide,
 * `allClients` surface) on the scope axis, and beside a future `/clientOperator` on the level axis. The old
 * name `userAdmin` described a topic, which stopped being right the moment anything but user administration
 * (the cfact discovery listing, #455) joined the section. `UADEP` keeps its name -- these paths still *do*
 * user administration; only the section they hang under was renamed.
 *
 * "Confined to a client" is the *widest* confinement this surface applies, not the only one: an administrator
 * who also carries a primary *organization* is narrowed one width further, to that org within the client (see
 * `ReadScopeRules.forCaller`). So a handler derives its scope from the caller rather than assuming the client --
 * the section name marks the authority, and the scope it resolves to is the caller's, which can be narrower.
 *
 * **This is the surface a frontend should call.** It serves both kinds of administrator correctly -- a caller
 * with `allClients` is simply unconfined -- so a console built on it needs no branch on who is asking.
 */
/**
 * The **read-only user directory** (issue #1432), in the `clientDirectory` section: the users of the caller's client,
 * listed and searched, for an editor -- who sees every user of its client and administers none. The same reads as
 * [UADEP.users] and [UADEP.userSearch], answering the same shape, with nothing beside them that changes a user.
 */
@Suppress("ConstPropertyName")
object CDEP {
    const val users = "/${SECT.clientDirectory}/users"
    const val userSearch = "/${SECT.clientDirectory}/userSearch"
}

@Suppress("ConstPropertyName")
object UADEP {
    const val users = "/${SECT.clientAdmin}/users"
    /** The scoped counterpart to [ADEP.userSearch] -- the brute-force cache search (issue #411). */
    const val userSearch = "/${SECT.clientAdmin}/userSearch"
    const val userCreate = "/${SECT.clientAdmin}/user/create"
    const val userSetRoles = "/${SECT.clientAdmin}/user/setRoles"
    const val userSetEnabled = "/${SECT.clientAdmin}/user/setEnabled"
    const val userSetOrg = "/${SECT.clientAdmin}/user/setOrg"
    const val userSetName = "/${SECT.clientAdmin}/user/setName"
    /** The scoped counterpart to [ADEP.userSetLabels]. */
    const val userSetLabels = "/${SECT.clientAdmin}/user/setLabels"
    /** The scoped counterpart to [ADEP.userLabelSuggestions]. */
    const val userLabelSuggestions = "/${SECT.clientAdmin}/user/labelSuggestions"
    /** The scoped counterpart to [ADEP.userInvite]. */
    const val userInvite = "/${SECT.clientAdmin}/user/invite"

    /** The scoped counterpart to [ADEP.userRemind]. */
    const val userRemind = "/${SECT.clientAdmin}/user/remind"
    /** The scoped counterpart to [ADEP.userIdentity]: the siblings listed are the ones in the caller's scope. */
    const val userIdentity = "/${SECT.clientAdmin}/user/identity"
    /** `DELETE`, like [ADEP.userDelete], and scoped to the caller's own client. */
    const val userDelete = "/${SECT.clientAdmin}/user"

    /**
     * The clients an administrator oversees, each with its status, origin, and counts (issue #904): every known
     * client for an `allClients` holder, their own for a client-scoped administrator. Here rather than beside
     * [ADEP.clientSummaries] because this is the surface both kinds of administrator reach.
     */
    const val clientsOverview = "/${SECT.clientAdmin}/clients/overview"

    /** The customers this deployment declares (issue #1114), for an administrator who sees every client. */
    const val customers = "/${SECT.clientAdmin}/customers"

    /**
     * One customer and its clients (issue #1114): any, for an administrator who sees every client; their own, for an
     * administrator of the customer's root client -- read-only, facts only.
     */
    const val customerOverview = "/${SECT.clientAdmin}/customer/overview"

    /**
     * Creates a client under the caller's customer (issue #1112): for an administrator of the customer's root client,
     * an id `<customer>_<name>`, with the creator's own admin user in it.
     */
    const val clientCreate = "/${SECT.clientAdmin}/client/create"

    /** The scoped counterpart to [ADEP.clientDefinition]: the caller's own client unless they may name another. */
    const val clientDefinition = "/${SECT.clientAdmin}/client/definition"

    /**
     * Adds or removes a client's Shadow Sandbox (issue #932): sets the `sandbox` flag of its stored definition,
     * publishes it, and reloads -- the caller's own client unless they may name another.
     */
    const val clientSandbox = "/${SECT.clientAdmin}/client/sandbox"

    /**
     * Recalculates the stored state of a client's forms, synchronously, for a client holding no more forms than its
     * page may recalculate (issue #1412) -- the caller's own client unless they may name another.
     */
    const val clientFormStatesRecalc = "/${SECT.clientAdmin}/client/formStates/recalculate"

    /**
     * Edits a client's definition from the Clients page (issue #1026): its presentation fields
     * (`ClientPresentationFields`), written to its stored definition and made to take effect as the copy and menu
     * editors' saves are -- live, or a draft for a client with a sandbox. The caller's own client unless they may
     * name another.
     */
    const val clientDefinitionSet = "/${SECT.clientAdmin}/client/definition/set"

    /**
     * The global traits a client may include (issue #1117), each with its description and its fields, and which the
     * client has customized. Scoped as [clientDefinition] is.
     */
    const val clientTraits = "/${SECT.clientAdmin}/client/traits"

    /**
     * Sets the traits a client includes (issue #1117): the `includedTraits` of its stored definition, made to take
     * effect as [clientDefinitionSet]'s saves do. Scoped as [clientDefinition] is.
     */
    const val clientTraitsSet = "/${SECT.clientAdmin}/client/traits/set"

    /**
     * Creates or edits one of a client's own traits from a design (issue #1118): a new trait in a configuration of its
     * own, an edit in the configuration that holds it, made to take effect as [clientDefinitionSet]'s saves do.
     * Scoped as [clientDefinition] is.
     */
    const val clientTraitDesign = "/${SECT.clientAdmin}/client/traits/design"

    /**
     * A client's workflows (issue #1120): the templates a workflow can be made from, and the client's own -- each made
     * from a template with its slots, or why it cannot be changed here. Scoped as [clientDefinition] is.
     */
    const val clientWorkflows = "/${SECT.clientAdmin}/client/workflows"

    /** Makes a workflow from a template for a client, or changes the slots of one made so (issue #1120). */
    const val clientWorkflowSave = "/${SECT.clientAdmin}/client/workflows/save"

    /**
     * Removes one of a client's workflows made from a template (issue #1346), where it is stored. Scoped as
     * [clientDefinition] is.
     */
    const val clientWorkflowRemove = "/${SECT.clientAdmin}/client/workflows/remove"

    /**
     * Sets the parameters of one of a client's normal workflows (issue #1356) -- its time windows, for now -- in the
     * client or sandbox named, at once, whoever declared the workflow.
     */
    const val clientWorkflowParams = "/${SECT.clientAdmin}/client/workflows/params"

    /**
     * A client's listing columns (issue #1491): each with where it is stored, or why it cannot be changed here, and the
     * traits a column may read, with their fields. Scoped as [clientDefinition] is.
     */
    const val clientUsages = "/${SECT.clientAdmin}/client/usages"

    /**
     * Adds one of a client's listing columns, or changes one where it is stored (issue #1491). Scoped as
     * [clientDefinition] is.
     */
    const val clientUsageSave = "/${SECT.clientAdmin}/client/usages/save"

    /** Removes one of a client's listing columns, where it is stored (issue #1491). Scoped as [clientDefinition] is. */
    const val clientUsageRemove = "/${SECT.clientAdmin}/client/usages/remove"

    /**
     * Narrows a global trait for a client (issue #1119): hides fields in its layout, requires fields, and restricts
     * choices, written as the client's alteration of the trait's data type. Scoped as [clientDefinition] is.
     */
    const val clientTraitNarrow = "/${SECT.clientAdmin}/client/traits/narrow"

    /**
     * The copy and interface one client's own configuration changes (issue #916) -- fragment keys and UiBlock
     * items, each with the value it replaces and the config that set it. Scoped as [clientDefinition] is.
     */
    const val clientOverrides = "/${SECT.clientAdmin}/client/overrides"

    /**
     * A client's web assets (issue #1130): every slot -- the tab icon, the brand mark -- with the metadata of the
     * image it holds and none of its content. Scoped as [clientDefinition] is.
     */
    const val clientWeb = "/${SECT.clientAdmin}/client/web"

    /** Uploads the image of one slot of a client's web assets, replacing the one there. Takes effect at once. */
    const val clientWebUpload = "$clientWeb/upload"

    /** Empties one slot of a client's web assets, so its people see the deployment's image again. */
    const val clientWebRemove = "$clientWeb/remove"

    /**
     * The image in one slot of a **named** client's web assets, as a file -- what an administrator looking at
     * another client's page previews, since the static address serves only the caller's own client's.
     */
    const val clientWebDownload = "$clientWeb/download"

    /**
     * What the image in one slot of a client's web assets **is** -- its metadata, by the download's own inputs --
     * without the image (issue #1131): the meta-only read beside [clientWebDownload].
     */
    const val clientWebDownloadMeta = "$clientWebDownload/meta"

    /**
     * A client's file refs (issue #1153): the named files its configuration holds -- stored, or shipped with it in
     * source code -- each with the record of its file and none of its content. Scoped as [clientDefinition] is.
     */
    const val clientFiles = "/${SECT.clientAdmin}/client/files"

    /** Uploads a file as one of a client's file refs: its bytes into the file store, its entry into the configuration. */
    const val clientFilesUpload = "$clientFiles/upload"

    /** Removes one of a client's file refs from its configuration. */
    const val clientFilesRemove = "$clientFiles/remove"

    /** Rewords, or clears, the description of one of a client's file refs (issue #1176), leaving its file as it is. */
    const val clientFilesDescribe = "$clientFiles/describe"

    /**
     * Puts back, in a client's draft, a file ref a draft removed (issue #1188): the entry its people are served,
     * exactly as it is published.
     */
    const val clientFilesRestore = "$clientFiles/restore"

    /** The file of one of a **named** client's file refs, as the listing shows it -- a draft included. */
    const val clientFilesDownload = "$clientFiles/download"

    /**
     * What the file of one of a client's file refs **is** -- its record, by the download's own inputs -- without
     * the file (issue #1131): the meta-only read beside [clientFilesDownload].
     */
    const val clientFilesDownloadMeta = "$clientFilesDownload/meta"

    /**
     * Names a local file as one of a client's file refs (issue #1164): a file under a root the deployment has
     * opened to clients, in the client's own directory there. No bytes are sent: the file is where it is.
     */
    const val clientFilesLocal = "$clientFiles/local"

    /** The roots of local files a client's own administrator may name a file in (issue #1164), by name. */
    const val clientFilesRoots = "$clientFiles/roots"

    /**
     * What a client's file refs take (issue #1176): how large a file may be and of what kinds, how many refs a
     * client is served, and the roots open to clients -- what a page needs before it offers to add one.
     */
    const val clientFilesTakes = "$clientFiles/takes"

    /** The named reports one client may run (issue #981), with how each column was bound. Scoped as [clientDefinition] is. */
    const val reports = "/${SECT.clientAdmin}/reports"

    /** What the report endpoints that act on one report sit under. */
    const val reportBase = "/${SECT.clientAdmin}/report"

    /** Runs one report over a client's forms (issue #981): a row per form or per group, cursor-paged. */
    const val reportRun = "$reportBase/run"

    /** A typed report endpoint's last segment (issue #1088): a row per form ([typedReportRun]) or per group. */
    const val typedReportRun = "run"
    const val typedReportAggregate = "aggregate"

    /**
     * Where one report's **typed** endpoint is served for one client (issue #1088):
     * `/clientAdmin/<client>/report/<reportId>/<action>`, [action] being [typedReportRun] or [typedReportAggregate].
     * Client and report are both in the path, so the catalog can say what that one report returns. One segment
     * longer than any shared report path, so the two never meet.
     */
    fun typedReportPath(client: String, reportId: String, action: String): String =
        clientPath("$reportBase/$reportId/$action", client)

    /** A report's stored snapshots, newest first and cursor-paged (issue #1034). Scoped as [reportRun] is, and client-wide. */
    const val reportHistory = "$reportBase/history"

    /** Takes a snapshot of a report's grouped run now (issue #1034). Scoped as [reportRun] is, and client-wide. */
    const val reportSnapshot = "$reportBase/snapshot"

    /** The report paths that bind for a client, for making a report from simple choices (issue #1492). */
    const val reportPaths = "$reportBase/paths"

    /** Makes a report from simple choices, in the client's `reports` configuration (issue #1492). */
    const val reportCreate = "$reportBase/create"

    /** Removes a report the client stores, wherever it is stored (issue #1492). */
    const val reportRemove = "$reportBase/remove"
}

/** Admin request/response field (JSON key) names. */
@Suppress("ConstPropertyName")
object ADF {
    const val userId = "userId"
    const val primaryId = "primaryId"
    const val username = "username"
    const val roles = "roles"
    const val org = "org"
    const val isEntity = "isEntity"
    const val name = "name"
    const val enabled = "enabled"

    /** On a create (issue #1504): whether to mail the invitation. Absent is true; a disabled user is never invited. */
    const val sendInvitation = "sendInvitation"

    /** In a create's answer (issue #1504): whether the invitation went. A mail that fails does not fail the create. */
    const val invitationSent = "invitationSent"

    /**
     * A user's free-form **labels** (issue #786): strings an administrator applies -- `reviewer`, say -- that a
     * workflow function can test for. Not roles: a label confers no privilege on any surface, it only lets a
     * workflow decide something about the person looking at it.
     */
    const val labels = "labels"

    /**
     * The labels a client's active users already carry (issue #1481), which a label editor offers beside the client's
     * own suggestions -- so a label typed for one user is offered for the next. Only the client's own list is what a
     * workflow may name.
     */
    const val labelsInUse = "labelsInUse"

    /**
     * In a sandbox (issue #1198): the roles the user **acts** with, which the sandbox's rules decide and [roles] --
     * what its row holds -- need not say (`SandboxAccess`). Absent outside a sandbox, where the two are one.
     */
    const val actingRoles = "actingRoles"

    /**
     * True for a sandbox user somebody opened (issue #1198), which acts as the user outside it was opened from and
     * has no roles or organization of its own to set; absent otherwise. Said whether or not [openedBy] is, since
     * that user may be one the caller does not administer.
     */
    const val opened = "opened"

    /**
     * For a sandbox user somebody opened (issue #1198): the id of the user outside the sandbox it was opened from,
     * which it acts as while that user still gives it access -- being the parent's, or an `allClients`
     * administrator's of any client -- with its roles, and its organization when it is the parent's. Given only
     * when the caller could know of that user anyway: it is the parent's, which a sandbox administrator is
     * trusted in by construction, or one the caller administers. Withheld otherwise, as the person view
     * withholds a user the caller cannot see.
     */
    const val openedBy = "openedBy"

    /** Beside [openedBy], and like it only when that is given: that user's client, while it still gives the sandbox user access. */
    const val actsAsClient = "actsAsClient"

    /** On the delete call: obfuscate the user's identity irrecoverably rather than merely disable them. */
    const val permanent = "permanent"

    /**
     * The client the user belongs to (issue #352).
     *
     * Read on the "create" call and reported on every user. **Create only**: a user's client cannot move
     * afterward without stranding their content, which carries the old client both in the `client` column and
     * inside every `GedraId` -- so there is no set-client call for this to name.
     */
    const val client = "client"
    const val hasPassword = "hasPassword"

    /**
     * The user's persona (issues #747, #750): read on the "create" call -- one of the `PERSONA` registry, `member`
     * when absent -- and reported on every user. **Create only**, like [client]: a persona is frozen at creation,
     * so there is no set-persona call.
     */
    const val persona = "persona"

    /**
     * The UAT batch discriminator (issue #747): read on the "create" call (empty when absent, and only needed for
     * a further user of the same address, client, and persona) and reported on every user. Create only, as part
     * of the user's key.
     */
    const val personaSuffix = "personaSuffix"

    /** Whether the account was permanently deleted -- an obfuscated tombstone that can no longer be edited. */
    const val deleted = "deleted"

    /** When it was permanently deleted, for a deleted account. */
    const val deletedAt = "deletedAt"

    /**
     * When the row was last updated (the `updatedAt` protocol column, surfaced on the admin projection for
     * issue #411): the default sort key of the cache search, and a column the console can order on.
     */
    const val updatedAt = "updatedAt"

    /** Case-insensitive substring filter applied to `primaryId`, `username` and `name` by the list endpoint. */
    const val search = "search"

    // The person behind a user (issue #770), on [ADTY.adminIdentity].
    /** When the identity's address was proven by a code or a link, or absent while unproven. */
    const val verifiedAt = "verifiedAt"
    /** The user an unnamed login at the address lands on, when it is one the caller may administer. */
    const val signsInAsUserId = "signsInAsUserId"
    /** The identity's users the caller may administer, the user asked about included, lowest id first. */
    const val users = "users"
}

/**
 * The **user-search** request fields and sort keys (issue #411) -- the brute-force search/sort over the user
 * cache, distinct from [ADF.search]'s single one-term-many-fields filter on the plain listing.
 *
 * Shared with the frontend, like the rest of this file, so the console builds its query from the same strings
 * the endpoint reads and a rename breaks compilation rather than a request. The four sort-key names
 * ([email], [publicName], [client], [updatedAt]) double as the values [sortBy] accepts and as the columns the
 * console offers to order on; adding a searchable/sortable attribute later is one entry here and one in the
 * backend `userSearchFields` registry.
 */
@Suppress("ConstPropertyName")
object USF {
    /** Case-insensitive **substring** of the email address. */
    const val email = "email"

    /** Case-insensitive **substring** of the public name (the username, or the email while still a placeholder). */
    const val publicName = "publicName"

    /**
     * Case-insensitive **substring** of the account's real-world name (a person's full name or a business's)
     * **or its username** -- the field matches either, so pasting a known username into the console's Name box
     * finds the account, the behavior the old single search box had. The same string as [ADF.name]; it filters
     * on the field the console shows as "Name", which is what an administrator types when searching for someone.
     */
    const val name = "name"

    /**
     * Case-insensitive **exact** persona (issue #750; a picker since #1432) -- `admin` finds a person's administrator
     * user. The personaSuffix (the `B` of a UAT batch) is matched by the free-text `q`, which also matches the persona.
     */
    const val persona = "persona"

    /**
     * Case-insensitive **exact** match of any one of the roles a user holds (issue #1432) -- `editor` finds every
     * editor, `admin` every administrator. The roles the row holds, not those it satisfies: an administrator satisfies
     * `editor` without holding it, and is found by `admin`. Not part of the free-text `q`, where `user` would match
     * every account.
     */
    const val roles = "roles"

    /**
     * Case-insensitive **exact** match of any one of the user's free-form labels (issues #786, #1481) -- `reviewer`
     * finds every reviewer. A label is stored case and all, but a search is a finding tool, so it ignores case as the
     * persona's does. Also part of the free-text `q`, so the one search box finds a user by a label as it does by a
     * name.
     */
    const val labels = "labels"

    /**
     * **Exact** client id to confine the search to. Only an `allClients` caller can widen past their own
     * client, so for anyone else this narrows within a client they are already confined to (or, naming
     * another, returns nothing) -- the scope does the enforcing, this only picks among what it allows.
     */
    const val client = "client"

    /**
     * The date attributes: each is a sort key, a field on every returned row, and a filterable range
     * (issue #462). See [UserDateKeys] for why they are generated from a root rather than written out.
     */
    val updated = UserDateKeys("updated", "updated")

    /**
     * When the person **proved this user is theirs** -- registered it (issue #749): a verification code used
     * for this user, the test fixture, or a user created by the person for themself. Absent for a user an
     * administrator provisioned that nobody has claimed yet, which is what keeps it off the default-user rule
     * and out of the switcher; cleared when a recoverably deleted user is provisioned again, since it then
     * registers afresh. Otherwise it never moves, and that is why the console does not show it: [activated]
     * answers the everyday question, and a column that rarely changes is noise in a list somebody is scanning.
     */
    val registered = UserDateKeys("registered", "registered")

    /**
     * When the account most recently **became active**: at creation, and again each time a disabled account is
     * re-enabled. Named for what it records rather than `registeredAt`, which would read as a permanent fact
     * about an account that re-enabling had quietly rewritten.
     */
    val activated = UserDateKeys("activated", "activated")

    /**
     * When a login sequence last **completed**. Presenting an existing session cookie does not count -- that
     * is not a login, and counting it would turn this into "last seen", which is a different question and one
     * every background poll would answer.
     */
    val lastLoggedIn = UserDateKeys("lastLoggedIn", "last logged in")

    /**
     * When the account was last **edited** -- anything that is not a login and not an activation.
     *
     * The administrative counterpart of [updated], and the reason both exist. Stamping a login writes the row,
     * so `updatedAt` moves whenever anybody signs in; left as the console's column, the Users page would
     * quietly have become "most recently active" and a busy account would sit at the top for good. So
     * `updatedAt` stays the storage-level "this row was written", useful precisely because nothing filters it,
     * and this is the one an administrator is asking about.
     */
    val lastEdited = UserDateKeys("lastEdited", "last edited")

    /** The `updatedAt` sort key, and the field carrying the update time on each returned row. */
    val updatedAt: String get() = updated.at

    /** Only users updated **at or after** this instant (ISO-8601); the low end of the date range. */
    val updatedAfter: String get() = updated.after

    /** Only users updated **at or before** this instant (ISO-8601); the high end of the date range. */
    val updatedBefore: String get() = updated.before

    /** Which attribute to sort by -- one of [email], [name], [publicName], [client], [updatedAt]. Defaults to [updatedAt]. */
    const val sortBy = "sortBy"

    /** Sort descending rather than ascending. Defaults to true (newest / Z-A first). */
    const val descending = "descending"

    /** The default (and fairly large) cap on how many users the search returns when the caller names no limit. */
    const val defaultLimit = 500
}

/**
 * Bounds on a user's labels (issue #786). Labels are free text on the user row, which every node holds in its
 * user cache, so an edit may not grow it without limit; generous for what a label is -- a role-like word.
 * In the kernel so the admin console can tell an administrator about a limit before the backend refuses it.
 */
@Suppress("ConstPropertyName")
object ULIM {
    /** The most labels one user may carry. */
    const val maxLabels = 32

    /** The longest a single label may be, in characters. */
    const val maxLabelLength = 64
}

/** Admin schema type names. */
@Suppress("ConstPropertyName")
object ADTY {
    const val adminUser = "AdminUser"

    /** The person behind a user (issue #770): identity facts and the users of it in the caller's scope. */
    const val adminIdentity = "AdminIdentity"

    /** What the label-suggestions read returns: a client and the labels it suggests (issue #786). */
    const val labelSuggestions = "UserLabelSuggestions"
}

/**
 * The three wire names a date attribute of a user carries, generated from one [root] (issue #462).
 *
 * A date is never just one key: it is something to **sort** on (`updatedAt`), and a **range** to filter by
 * (`updatedAfter` / `updatedBefore`), and those three names always stand in the same relation. Written out by
 * hand they are three constants per date that have to agree, and five dates would be fifteen of them --
 * fifteen chances for a `lastLoggedInBefore` to be spelled `lastLoginBefore` on one side of the wire.
 *
 * The convention is not invented here. `updated` / `updatedAt` / `updatedAfter` / `updatedBefore` already
 * existed and already followed it exactly; this only stops the pattern being retyped.
 *
 * **In the kernel because both sides need the same strings.** The console builds its query and reads its
 * columns from these, so generating them here means the frontend derives what the backend serves rather than
 * carrying a parallel list that agrees until it does not. What cannot live here is how a date is *read off a
 * row* -- that needs `AuthUserRow`, and so pairs with these keys in `base/common`'s `userDateFields`.
 *
 * [phrase] is the human wording the endpoint's own descriptions are built from ("Only users **last edited**
 * at or after this time"), so the copy follows the same root as the keys.
 */
class UserDateKeys(val root: String, val phrase: String) {
    /** The sort key, and the field carrying this date on every returned row. */
    val at: String get() = root + "At"

    /** Only users whose date is **at or after** this instant; the low end of the range. */
    val after: String get() = root + "After"

    /** Only users whose date is **at or before** this instant; the high end of the range. */
    val before: String get() = root + "Before"
}
